import cx from 'classnames';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, ButtonVariants } from '@components/button/Button';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import { Icon } from '@components/icon/Icon';
import { errorToast, successToast } from '@components/toasts/Toasts';
import { ApiError } from '@shared/api/errors';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { reportAppError } from '@shared/reportAppError';
import { updateReservationNote } from 'webReservations/reservations/apiHelpers';
import typography from '~styles/typography.scss';
import styles from './ReservationNote.scss';

interface ReservationNoteFormData {
  note: string;
}

export const ReservationNote = ({
  reservationId,
  note,
}: {
  reservationId: string;
  note?: string;
}) => {
  const [reservationNote, setReservationNote] = useState<string | undefined>(
    note,
  );
  const { isOpen: isFormOpen, open: openForm, close: closeForm } = useIsOpen();

  const { control, handleSubmit } = useForm<ReservationNoteFormData>({
    defaultValues: { note: note || '' },
  });

  const saveNote = async (data: ReservationNoteFormData) => {
    try {
      await updateReservationNote(reservationId, data.note || null);
      setReservationNote(data.note);
      closeForm();
      successToast({
        message: 'Successfully added notes to your reservation.',
      });
    } catch (error) {
      if (error instanceof ApiError) {
        errorToast({ message: error.message });
      } else {
        errorToast({
          message: 'Failed to add notes to your reservation. Please try again.',
        });
      }
      reportAppError(error);
    }
  };

  return (
    <div className={styles.container}>
      <header>
        <h2 className={typography.h7m}>Notes to Restaurant</h2>
        {!isFormOpen && (
          <button
            className={cx(typography.t3, styles.addNoteButton)}
            onClick={openForm}
            type="button"
          >
            <Icon name="pencil" />
            Add Notes
          </button>
        )}
      </header>
      {isFormOpen ? (
        <form onSubmit={handleSubmit(saveNote)}>
          <ControlledFormTextareaAutosize
            control={control}
            label="Note"
            name="note"
            rules={{
              maxLength: {
                message: 'Your note must be 600 characters or less',
                value: 600,
              },
            }}
          />
          <div className={styles.formActions}>
            <Button
              type="button"
              label="Cancel"
              onClick={closeForm}
              variant={ButtonVariants.Tertiary}
            />
            <Button
              type="submit"
              label="Save Notes"
              variant={ButtonVariants.Primary}
            />
          </div>
        </form>
      ) : (
        <p
          className={cx({
            [typography.c3_20]: true,
            [styles.withReservationNote]: !!reservationNote,
          })}
        >
          {reservationNote || 'No notes for this visit.'}
        </p>
      )}
    </div>
  );
};
